import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';

const KycVerification = () => {
  const dispatch = useDispatch();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { fetchPendingKyc(); }, []);

  const fetchPendingKyc = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.get('/admin/kyc/pending');
      setPendingUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch pending KYC:', error);
      dispatch(showToast({ message: 'Failed to load KYC data', type: 'error' }));
    } finally { setIsLoading(false); }
  };

  const handleApprove = async (userId, documentType) => {
    setProcessingIds(prev => [...prev, `${userId}-${documentType}`]);
    try {
      await adminApi.post(`/admin/kyc/${userId}/approve`, { documentType });
      dispatch(showToast({ message: `${documentType.toUpperCase()} approved for user`, type: 'success' }));
      fetchPendingKyc();
    } catch (error) { dispatch(showToast({ message: 'Failed to approve document', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(id => id !== `${userId}-${documentType}`)); }
  };

  const handleReject = async (userId, documentType) => {
    if (!rejectReason.trim()) { dispatch(showToast({ message: 'Please provide a rejection reason', type: 'error' })); return; }
    setProcessingIds(prev => [...prev, `${userId}-${documentType}`]);
    try {
      await adminApi.post(`/admin/kyc/${userId}/reject`, { documentType, reason: rejectReason });
      dispatch(showToast({ message: `${documentType.toUpperCase()} rejected`, type: 'error' }));
      setRejectReason('');
      fetchPendingKyc();
    } catch (error) { dispatch(showToast({ message: 'Failed to reject document', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(id => id !== `${userId}-${documentType}`)); }
  };

  const getDocIcon = (type) => {
    const icons = { id: 'fa-passport', address: 'fa-file-alt', bank: 'fa-bank', selfie: 'fa-camera' };
    return icons[type] || 'fa-file';
  };

  const getDocName = (type) => {
    const names = { id: 'Government ID', address: 'Proof of Address', bank: 'Bank Statement', selfie: 'Selfie with ID' };
    return names[type] || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">KYC VERIFICATION</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">Review and verify user documents</p>
        </div>
        <button onClick={fetchPendingKyc} 
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
          <i className="fas fa-sync-alt mr-1.5" /> REFRESH
        </button>
      </div>

      {pendingUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-2xl bg-[#ecfdf5] border-2 border-[#a7f3d0] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-check-circle text-[#059669] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">ALL CLEAR!</p>
          <p className="text-[#94a3b8] text-[11px] font-bold">No pending KYC verifications</p>
        </div>
      ) : (
        <div className="divide-y-2 divide-[#f1f5f9]">
          {pendingUsers.map((user) => (
            <div key={user.userId} className="p-5 bg-white hover:bg-[#f8fafc] transition-all">
              {/* User Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25">
                  <span className="text-sm font-extrabold text-white">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <div className="font-syne text-sm font-extrabold text-[#0f172a]">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-[10px] text-[#64748b] font-mono font-bold">{user.email}</div>
                </div>
                {user.phone && (
                  <div className="ml-auto text-[10px] text-[#475569] font-mono font-extrabold bg-[#f8fafc] border-2 border-[#e2e8f0] px-3 py-1 rounded-lg">
                    <i className="fas fa-phone mr-1.5 text-[#1e3a5f]" />{user.phone}
                  </div>
                )}
              </div>

              {/* Documents Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {user.documents.map((doc) => (
                  <div key={doc.id} className="bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-4 hover:border-[#1e3a5f] hover:shadow-md transition-all">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-[#eff6ff] flex items-center justify-center text-[#1e3a5f]">
                        <i className={`fas ${getDocIcon(doc.type)} text-sm`} />
                      </div>
                      <span className="text-[11px] font-syne font-extrabold text-[#0f172a] uppercase tracking-wider">{getDocName(doc.type)}</span>
                    </div>
                    <div className="text-[9px] text-[#64748b] font-mono font-bold mb-2 truncate bg-white border border-[#e2e8f0] rounded-lg px-2.5 py-1.5">
                      <i className="fas fa-file mr-1.5 text-[#94a3b8]" />{doc.filename}
                    </div>
                    <div className="text-[8px] text-[#94a3b8] font-mono font-bold mb-4 flex items-center gap-1.5">
                      <i className="fas fa-calendar text-[8px]" />
                      Uploaded: {new Date(doc.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleApprove(user.userId, doc.type)}
                        disabled={processingIds.includes(`${user.userId}-${doc.type}`)}
                        className="flex-1 py-2 rounded-lg bg-[#059669] text-white text-[9px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all disabled:opacity-50 border-b-[2px] border-[#047857]">
                        {processingIds.includes(`${user.userId}-${doc.type}`) ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <><i className="fas fa-check mr-1" /> APPROVE</>
                        )}
                      </button>
                      <button onClick={() => { setSelectedUser({ userId: user.userId, docType: doc.type }); setRejectReason(''); }}
                        disabled={processingIds.includes(`${user.userId}-${doc.type}`)}
                        className="flex-1 py-2 rounded-lg bg-[#dc2626] text-white text-[9px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[2px] border-[#b91c1c]">
                        <i className="fas fa-times mr-1" /> REJECT
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Approve All Button */}
              <div className="mt-4">
                <button onClick={async () => { for (const doc of user.documents) { await handleApprove(user.userId, doc.type); } }}
                  className="py-2.5 px-5 rounded-xl bg-[#059669] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[3px] border-[#047857]">
                  <i className="fas fa-check-double mr-1.5" /> APPROVE ALL DOCUMENTS
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Reason Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedUser(null)}>
          <div className="relative bg-white border-2 border-[#dc2626] rounded-2xl w-full max-w-[440px] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b-2 border-[#e2e8f0] bg-[#fef2f2]">
              <h3 className="font-syne text-lg font-extrabold text-[#7f1d1d] uppercase tracking-tight">REJECT DOCUMENT</h3>
              <p className="text-[10px] text-[#991b1b] font-mono font-bold mt-0.5 uppercase tracking-wider">Provide a reason for rejection</p>
            </div>
            <div className="p-6">
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Document is blurry or expired..."
                rows={4}
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] font-dm text-sm font-extrabold outline-none focus:border-[#dc2626] focus:bg-white transition-all resize-none placeholder:text-[#94a3b8]" />
              <div className="flex gap-3 mt-5">
                <button onClick={() => setSelectedUser(null)}
                  className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#f1f5f9] transition-all">
                  CANCEL
                </button>
                <button onClick={() => { handleReject(selectedUser.userId, selectedUser.docType); setSelectedUser(null); }}
                  disabled={!rejectReason.trim()}
                  className="flex-1 py-3 rounded-xl bg-[#dc2626] text-white font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[3px] border-[#b91c1c]">
                  CONFIRM REJECT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KycVerification;